<template>
  <ui-overlay-loader :loading="documentUpdating">
    <div class="heading-container">
      <ui-heading tag="h3" no-top-margin>
        {{ $t("document_form_heading") }}
      </ui-heading>
      <ui-heading tag="h3" no-top-margin class="inline-heading">
        <div class="icons">
          <span v-if="documentHasQr">
            {{ $t("icons.qr") }}
            <icon :title="$t('icons.qr-code')" name="qr-code" class="icon" />
          </span>
          <span v-if="isNFCScanned">
            {{ $t("icons.nfc") }}
            <icon :title="$t('icons.nfc')" name="nfc" class="icon" />
          </span>
          <span v-if="isOriginalCharactersCheckEnabled">
            <icon
              :title="originalCharactersCheckIconTitle"
              :name="originalCharactersCheckIconName"
              :class="originalCharactersIconColor"
              class="icon"
            />
          </span>
        </div>
      </ui-heading>
    </div>
    <form
      v-if="document"
      @submit.prevent="saveDocument"
      autocomplete="off"
      novalidate
    >
      <div class="field-list">
        <div
          class="field-item"
          v-for="{
            key,
            displayValue,
            label,
            type,
            options,
            filterable,
            placeholder,
            required,
            min,
            max,
            minlength,
            maxlength,
            pattern,
            variant,
            error
          } in documentFormVisible"
          :key="key"
        >
          <ui-field
            v-if="!isDocumentEditEnabled"
            label-position="inline"
            :label="label"
            bottom-margin="small"
            :variant="variant"
          >
            {{
              displayValue ||
              (options && formatValueLabel(document[key], options)) ||
              document[key] ||
              "-"
            }}
          </ui-field>
          <ui-field-input
            v-else-if="isInput(type)"
            label-position="inline"
            :label="label"
            :id="key"
            :type="type"
            v-model.trim="document[key]"
            :disabled="actionInProgress"
            :placeholder="placeholder"
            :required="required"
            :pattern="pattern"
            :min="min"
            :max="max"
            :minlength="minlength"
            :maxlength="maxlength"
            bottom-margin="small"
            :variant="variant"
            :error="error"
          />
          <ui-field-select
            v-else-if="isSelect(type)"
            label-position="inline"
            :label="label"
            :id="key"
            :required="required"
            :options="options"
            :filterable="filterable"
            type="text"
            v-model="document[key]"
            :disabled="actionInProgress"
            bottom-margin="small"
            :variant="variant"
            :error="error"
          />
          <ui-field-text-area
            v-else-if="isTextArea(type)"
            label-position="inline"
            bottom-margin="small"
            rows="3"
            :label="label"
            :id="key"
            :minlength="minlength"
            :maxlength="maxlength"
            :required="required"
            :disabled="actionInProgress"
            :variant="variant"
            :error="error"
            v-model="document[key]"
          />
        </div>
      </div>
      <mrz-data
        :is-document-edit-enabled="isDocumentEditEnabled"
        :field-values="mrzFieldValues"
        :action-in-progress="actionInProgress"
        :document-updating="documentUpdating"
        :current-identification="currentIdentification"
        @field-change="onMrzFieldChange"
      />
      <ui-button-group>
        <ui-button
          ref="editButton"
          type="button"
          v-show="!isDocumentEditEnabled && shouldEditBeVisible"
          @click="onEditClick"
          variant="primary"
        >
          {{ $t("document_form_buttons.edit") }}
        </ui-button>
        <ui-button
          type="submit"
          variant="primary"
          v-show="isDocumentEditEnabled"
          :disabled="actionInProgress"
        >
          {{ $t("document_form_buttons.submit") }}
        </ui-button>
        <ui-button
          type="button"
          v-show="isDocumentEditEnabled"
          :disabled="actionInProgress"
          @click="onEditCancel"
        >
          {{ $t("document_form_buttons.cancel") }}
        </ui-button>
      </ui-button-group>
    </form>
  </ui-overlay-loader>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { isEqual } from "lodash";
import {
  COUNTRY_CODES,
  DOCUMENT_FIELDS,
  DOCUMENT_TYPE as PROCEDURES_DOCUMENT_TYPE,
  DOCUMENT_TYPES,
  GENDER_OPTIONS,
  INPUT_TYPE,
  NOT_SPECIFIED_CODES,
  STATES
} from "@/constants";
import { actions, getters } from "@/store";
import { documentDateFormat } from "@/utils/date";
import { getDocumentValidation } from "@/utils/form";
import { getDocumentFieldError } from "@/utils/validations";
import { getCountryISOCode } from "@/utils/countries";
import {
  getDocumentSubType,
  getDocumentSubTypes
} from "@/utils/document";
import { MRZ_FIELDS } from "@/constants/mrz";
import { isNFCScanned } from "../utils/documentPhoto";
import MrzData from "./MrzData";

const DATE_PLACEHOLDER = "YYYY-MM-DD";

export default {
  props: {
    complianceReviewIdentificationId: { type: String, default: null }
  },

  data() {
    const initializedMrzFields = Object.values(MRZ_FIELDS).reduce(
      (accumulator, fieldKey) => {
        accumulator[fieldKey] = "";
        return accumulator;
      },
      {}
    );

    return {
      document: null,
      initialDocument: null,
      MRZ_FIELDS,
      originalMrzFieldValues: null,
      mrzFieldValues: initializedMrzFields
    };
  },
  computed: {
    ...mapState({
      work: state => state.work
    }),
    ...mapGetters([
      getters.currentIdentification,
      getters.currentDocumentData,
      getters.currentDocumentValidity,
      getters.actionInProgress,
      getters.documentUpdating,
      getters.isDocumentEditEnabled,
      getters.documentHasQr,
      getters.isComplianceReviewItemResolved
    ]),
    shouldEditBeVisible() {
      if (
        !!this.complianceReviewIdentificationId &&
        this.isComplianceReviewItemResolved
      ) {
        return false;
      }

      return true;
    },
    cleanDocument() {
      return (
        this.document &&
        Object.entries(this.document).reduce((acc, [key, value]) => {
          if (value) {
            acc[key] = value;
          }
          return acc;
        }, {})
      );
    },
    documentPayload() {
      return {
        ...this.cleanDocument,
        ...this.mrzFieldValues,
        [DOCUMENT_FIELDS.DOCUMENT_SUB_TYPE]: getDocumentSubType(
          this.document?.[DOCUMENT_FIELDS.DOC_TYPE],
          this.document?.[DOCUMENT_FIELDS.DOCUMENT_SUB_TYPE]
        )
      };
    },
    documentTypeOptions() {
      return Object.values(DOCUMENT_TYPES).map(type => ({
        value: type,
        label: this.$t(`document_type.${type}`)
      }));
    },
    documentSubTypeOptions() {
      const docType = this.document?.[DOCUMENT_FIELDS.DOC_TYPE];

      return [
        { value: "", label: this.$t("not_specified") },
        ...getDocumentSubTypes(docType).map(subType => ({
          value: subType,
          label: this.$t(`document_sub_type.${subType}`)
        }))
      ];
    },
    documentSubTypeDisplayValue() {
      return this.translateValue(
        "document_sub_type",
        this.document?.[DOCUMENT_FIELDS.DOCUMENT_SUB_TYPE]
      );
    },
    countryOptions() {
      return Object.values(COUNTRY_CODES)
        .map(country => ({
          value: country,
          label: `${this.$t(`countries.${country}`)} (${country})`
        }))
        .sort(({ label: a }, { label: b }) =>
          a.localeCompare(b, this.$i18n.locale)
        );
    },
    notSpecifiedOptions() {
      return NOT_SPECIFIED_CODES.map(not_specified_code => ({
        label: `${this.$t("not_specified")} (${not_specified_code})`,
        value: not_specified_code
      })).sort(({ label: a }, { label: b }) =>
        a.localeCompare(b, this.$i18n.locale)
      );
    },
    stateOptions() {
      const countryCode = this.document?.[DOCUMENT_FIELDS.COUNTRY];
      const states = STATES[countryCode] ?? {};
      return Object.values(states)
        .map(state => ({
          value: state,
          label: `${this.$t(
            ["states", countryCode, state].join(".")
          )} (${state})`
        }))
        .sort(({ label: a }, { label: b }) =>
          a.localeCompare(b, this.$i18n.locale)
        );
    },
    documentFormFull() {
      const { document } = this;
      const validation = getDocumentValidation(document);
      return [
        {
          key: DOCUMENT_FIELDS.FIRST_NAME,
          label: this.$t("document_form.first_name"),
          type: INPUT_TYPE.TEXT,
          ...validation[DOCUMENT_FIELDS.FIRST_NAME]
        },
        {
          key: DOCUMENT_FIELDS.LAST_NAME,
          label: this.$t("document_form.last_name"),
          type: INPUT_TYPE.TEXT,
          ...validation[DOCUMENT_FIELDS.LAST_NAME]
        },
        {
          key: DOCUMENT_FIELDS.GENDER,
          label: this.$t("document_form.gender"),
          type: INPUT_TYPE.SELECT,
          options: Object.values(GENDER_OPTIONS).map(gender => ({
            value: gender,
            label: `${this.$t(`gender.${gender}`)} (${gender})`
          })),
          ...validation[DOCUMENT_FIELDS.GENDER]
        },
        {
          key: DOCUMENT_FIELDS.DATE_OF_BIRTH,
          displayValue: this.isoDateToDisplay(
            document?.[DOCUMENT_FIELDS.DATE_OF_BIRTH]
          ),
          label: this.$t("document_form.date_of_birth"),
          type: INPUT_TYPE.TEXT,
          placeholder: DATE_PLACEHOLDER,
          ...validation[DOCUMENT_FIELDS.DATE_OF_BIRTH]
        },
        {
          key: DOCUMENT_FIELDS.PLACE_OF_BIRTH,
          label: this.$t("document_form.place_of_birth"),
          type: INPUT_TYPE.TEXT,
          hidden: this.isPlaceOfBirthFieldHidden,
          ...validation[DOCUMENT_FIELDS.PLACE_OF_BIRTH]
        },
        {
          key: DOCUMENT_FIELDS.COUNTRY,
          label: this.$t("document_form.country"),
          type: INPUT_TYPE.SELECT,
          filterable: true,
          options: [...this.notSpecifiedOptions, ...this.countryOptions],
          ...validation[DOCUMENT_FIELDS.COUNTRY]
        },
        {
          key: DOCUMENT_FIELDS.STATE,
          label: this.$t("document_form.state"),
          type: INPUT_TYPE.SELECT,
          filterable: true,
          options: this.stateOptions,
          hidden: this.isDocumentStateFieldHidden,
          ...validation[DOCUMENT_FIELDS.STATE]
        },
        {
          key: DOCUMENT_FIELDS.ADDRESS,
          label: this.$t("document_form.address"),
          type: INPUT_TYPE.TEXT_AREA,
          hidden: this.isDocumentAddressFieldHidden,
          ...validation[DOCUMENT_FIELDS.ADDRESS]
        },
        {
          key: DOCUMENT_FIELDS.NATIONALITY,
          label: this.$t("document_form.nationality"),
          type: INPUT_TYPE.SELECT,
          filterable: true,
          options: [...this.notSpecifiedOptions, ...this.countryOptions],
          ...validation[DOCUMENT_FIELDS.NATIONALITY]
        },
        {
          key: DOCUMENT_FIELDS.PERSONAL_CODE,
          label: this.$t("document_form.personal_code"),
          type: INPUT_TYPE.TEXT,
          ...validation[DOCUMENT_FIELDS.PERSONAL_CODE]
        },
        {
          key: DOCUMENT_FIELDS.DOC_TYPE,
          label: this.$t("document_form.doc_type"),
          type: INPUT_TYPE.SELECT,
          options: this.documentTypeOptions,
          ...validation[DOCUMENT_FIELDS.DOC_TYPE]
        },
        {
          key: DOCUMENT_FIELDS.DOCUMENT_SUB_TYPE,
          label: this.$t("document_form.document_sub_type"),
          displayValue: this.documentSubTypeDisplayValue,
          type: INPUT_TYPE.SELECT,
          options: this.documentSubTypeOptions,
          hidden: this.isDocumentSubTypeFieldHidden,
          ...validation[DOCUMENT_FIELDS.DOCUMENT_SUB_TYPE]
        },
        {
          key: DOCUMENT_FIELDS.DOC_NUMBER,
          label: this.$t("document_form.doc_number"),
          type: INPUT_TYPE.TEXT,
          ...validation[DOCUMENT_FIELDS.DOC_NUMBER]
        },
        {
          key: DOCUMENT_FIELDS.DATE_OF_ISSUE,
          displayValue: this.isoDateToDisplay(
            document?.[DOCUMENT_FIELDS.DATE_OF_ISSUE]
          ),
          label: this.$t("document_form.date_of_issue"),
          type: INPUT_TYPE.TEXT,
          placeholder: DATE_PLACEHOLDER,
          ...validation[DOCUMENT_FIELDS.DATE_OF_ISSUE]
        },
        {
          key: DOCUMENT_FIELDS.DATE_OF_EXPIRATION,
          displayValue: this.isoDateToDisplay(
            document?.[DOCUMENT_FIELDS.DATE_OF_EXPIRATION]
          ),
          label: this.$t("document_form.date_of_expiration"),
          type: INPUT_TYPE.TEXT,
          placeholder: DATE_PLACEHOLDER,
          ...validation[DOCUMENT_FIELDS.DATE_OF_EXPIRATION]
        },
        {
          key: DOCUMENT_FIELDS.CATEGORY,
          label: this.$t("document_form.category"),
          hidden: this.isDocumentCategoryFieldHidden,
          type: INPUT_TYPE.TEXT,
          ...validation[DOCUMENT_FIELDS.CATEGORY]
        }
      ].map(field => {
        const value = document?.[field.key];
        const error = getDocumentFieldError(
          field.key,
          value,
          validation[field.key],
          this.currentDocumentValidity?.[field.key]
        );
        const apiValidation = this.currentDocumentValidity?.[field.key];
        let variant = "default";
        if (apiValidation === false) {
          variant = "error";
        } else if (apiValidation === true) {
          variant = "success";
        }
        return {
          ...field,
          variant,
          error: error && this.$t(`input_error.${error}`, validation[field.key])
        };
      });
    },
    isDocumentStateFieldHidden() {
      const { USA, CAN, MEX } = COUNTRY_CODES;
      const { DRIVER_LICENSE } = DOCUMENT_TYPES;
      const country = this.document?.[DOCUMENT_FIELDS.COUNTRY];
      const docType = this.document?.[DOCUMENT_FIELDS.DOC_TYPE];
      // Should not be hidden for MEX+DriverLicense
      if (country === MEX) return DRIVER_LICENSE !== docType;
      // Should not be hidden for USA or CAN
      return ![USA, CAN].includes(country);
    },

    isPlaceOfBirthFieldHidden() {
      return this.document?.[DOCUMENT_FIELDS.PLACE_OF_BIRTH] == null;
    },

    isDocumentAddressFieldHidden() {
      const {
        ROU,
        JAM,
        DEU,
        ITA,
        FRA,
        ESP,
        HRV,
        CZE,
        MDV,
        BGR,
        SVK,
        POL
      } = COUNTRY_CODES;
      const { DRIVER_LICENSE, ID_CARD } = DOCUMENT_TYPES;
      const country = this.document?.[DOCUMENT_FIELDS.COUNTRY];
      const docType = this.document?.[DOCUMENT_FIELDS.DOC_TYPE];

      if (country === ROU || country === DEU) {
        // any document
        return false;
      } else if ([ITA, FRA, ESP, HRV, MDV, BGR].includes(country)) {
        // only ID card
        return ![ID_CARD].includes(docType);
      } else if ([JAM, CZE, SVK, POL].includes(country)) {
        // ID card and Driver license
        return ![DRIVER_LICENSE, ID_CARD].includes(docType);
      }
      return true;
    },

    isDocumentSubTypeFieldHidden() {
      const docType = this.document?.[DOCUMENT_FIELDS.DOC_TYPE];

      if (this.isDocumentEditEnabled) {
        return getDocumentSubTypes(docType).length === 0;
      }

      return !this.document?.[DOCUMENT_FIELDS.DOCUMENT_SUB_TYPE];
    },

   isDocumentCategoryFieldHidden() {
      const { DRIVER_LICENSE } = DOCUMENT_TYPES;
      const docType = this.document?.[DOCUMENT_FIELDS.DOC_TYPE];

      return docType !== DRIVER_LICENSE;
    },

    documentFormVisible() {
      return this.documentFormFull.filter(({ hidden }) => !hidden);
    },
    documentFormHasError() {
      return this.documentFormVisible.some(({ error }) => error);
    },
    isNFCScanned() {
      return isNFCScanned(this.currentIdentification?.data?.media);
    },
    originalCharactersCheckIconTitle() {
      return this.isOriginalCharactersCheckActive
        ? this.$t("icons.originalCharactersCheckActive")
        : this.$t("icons.originalCharactersCheckInactive");
    },
    originalCharactersCheckIconName() {
      return this.isOriginalCharactersCheckActive
        ? "original-characters-check-active"
        : "original-characters-check-inactive";
    },
    originalCharactersIconColor() {
      return this.isOriginalCharactersCheckActive
        ? "icon-color-active"
        : "icon-color-inactive";
    },
    useOriginalCharacter() {
      return this.currentIdentification?.data?.procedures?.acceptableDocuments?.find(
        document => this.isDocumentMatch(document.documentType)
      )?.useOriginalCharacter;
    },

    isOriginalCharactersCheckEnabled() {
      return !!this.useOriginalCharacter?.enabled;
    },
    isOriginalCharactersCheckActive() {
      if (!this.isOriginalCharactersCheckEnabled) {
        return false;
      }

      return !!this.useOriginalCharacter.countriesIso2?.find(
        country =>
          country.toUpperCase() ===
          getCountryISOCode(this.currentDocumentData?.country)
      );
    }
  },
  methods: {
    ...mapActions([actions.putIdentificationDocument, actions.notificationAdd]),
    ...mapMutations({
      changeDocumentEditStatus: "changeDocumentEditStatus"
    }),
    isoDateToDisplay(isoDate) {
      return isoDate && documentDateFormat(new Date(`${isoDate}T00:00`), false);
    },
    isInput(type) {
      return [INPUT_TYPE.TEXT, INPUT_TYPE.DATE].includes(type);
    },
    isSelect(type) {
      return type === INPUT_TYPE.SELECT;
    },
    isTextArea(type) {
      return type === INPUT_TYPE.TEXT_AREA;
    },
    onEditClick() {
      if (!this.isDocumentEditEnabled) {
        this.$store.commit("changeDocumentEditStatus", true);
        this.$nextTick(() => {
          // focus first input
          document.getElementById(this.documentFormVisible[0]?.key)?.focus?.();
        });

        this.originalMrzFieldValues = { ...this.mrzFieldValues };
      }
    },
    onEditCancel() {
      this.mrzFieldValues = { ...this.originalMrzFieldValues };
      this.originalMrzFieldValues = null;
      this.updateDocument();
      this.$store.commit("changeDocumentEditStatus", false);
      this.$nextTick(() => {
        this.$refs.editButton.$el.focus();
      });
    },
    updateDocument() {
      const data = this.currentDocumentData;
      if (!data) return;
      const documentMainData = this.documentFormFull.reduce(
        (document, { key }) => {
          document[key] = data[key];
          return document;
        },
        {}
      );

      this.document = { ...documentMainData, ...this.mrzFieldValues };

      this.initialDocument = this.documentPayload;
    },

    setInitialMrzFieldValues(mrzValues) {
      if (!mrzValues) {
        return;
      }

      for (const fieldName of Object.keys(mrzValues)) {
        const valueData = mrzValues?.[fieldName];

        this.mrzFieldValues[fieldName] = valueData || "";
      }
    },

    onMrzFieldChange({ fieldName, value }) {
      this.mrzFieldValues[fieldName] = value;
    },

    saveDocument() {
      if (this.documentFormHasError) {
        const { key, error, label } = this.documentFormVisible.find(
          ({ error }) => error
        );
        document.getElementById(key).focus();
        this.notificationAdd({
          id: `document-validation-error-${key}`,
          title: "Document is not valid",
          message: `${label}: ${error}`
        });
      } else {
        const updatedDocument = this.documentPayload;
        const shouldUpdate = !isEqual(updatedDocument, this.initialDocument);

        if (shouldUpdate) {
          this.putIdentificationDocument({
            identificationId: this.work.identificationId,
            document: updatedDocument
          });

          this.initialDocument = updatedDocument;
        } else {
          this.$store.commit("changeDocumentEditStatus", false);
        }
      }
    },
    translateValue(prefix, value) {
      if (!value) {
        return null;
      }

      const key = `${prefix}.${value}`;
      const isTranslated =
        this.$te(key) || this.$te(key, this.$i18n.fallbackLocale);

      return isTranslated ? this.$t(key) : value;
    },
    formatValueLabel(value, options) {
      const option = options.find(
        ({ value: optionValue }) => optionValue === value
      );
      return option?.label || option?.value;
    },
    isDocumentMatch(documentType) {
      // TODO: when BE will make changes to idCard, driverLicense renames - remove this
      if (documentType === PROCEDURES_DOCUMENT_TYPE.ID_CARD) {
        return (
          this.currentDocumentData?.docType ===
            PROCEDURES_DOCUMENT_TYPE.ID_CARD ||
          this.currentDocumentData?.docType === DOCUMENT_TYPES.ID_CARD
        );
      }
      if (documentType === PROCEDURES_DOCUMENT_TYPE.DRIVER_LICENSE) {
        return (
          this.currentDocumentData?.docType ===
            PROCEDURES_DOCUMENT_TYPE.DRIVER_LICENSE ||
          this.currentDocumentData?.docType === DOCUMENT_TYPES.DRIVER_LICENSE
        );
      }

      return documentType === this.currentDocumentData?.docType;
    }
  },
  components: {
    MrzData
  },
  watch: {
    "currentIdentification.data.document.data": {
      handler({ mrzLine1, mrzLine2, mrzLine3 }) {
        this.setInitialMrzFieldValues({ mrzLine1, mrzLine2, mrzLine3 });
        this.$nextTick(() => {
          this.updateDocument();
        });
        this.$store.commit("changeDocumentEditStatus", false);
      },
      immediate: true
    }
  }
};
</script>

<style scoped lang="scss">
.icons {
  display: flex;
  gap: 0.5rem;
}
.icon {
  width: 2rem;
  margin-left: 0.25rem;
}
.icon-color-active {
  color: var(--icon-active-color);
}
.icon-color-inactive {
  color: var(--icon-inactive-color);
}
.heading-container {
  display: flex;
  justify-content: space-between;
}
.inline-heading,
.inline-heading .icons span {
  display: flex;
  align-items: center;
  font-size: 1.25rem;
}

.field-list {
  display: flex;
  flex-wrap: wrap;
}
.field-item {
  flex: 1 1 auto;
  width: 100%;

  ::v-deep input,
  ::v-deep textarea,
  ::v-deep select,
  ::v-deep .field-input {
    text-transform: uppercase;
  }
}
</style>
